/**
 * Centralized currency configuration for the entire application.
 *
 * Two scopes (UK localisation Phase 1 — docs/UK_LOCALISATION_PLAN.md):
 *
 * 1. LOGIN scope (localStorage, shared across tabs): set from the /auth/signin
 *    response for vendors/stewards/customers. Survives reloads for the logged-in
 *    dashboard experience.
 *
 * 2. VENDOR scope (sessionStorage, THIS TAB ONLY): set from the guest QR menu
 *    payload so an unauthenticated guest sees the restaurant's currency. Kept
 *    per-tab deliberately — a shared device (steward tablet, family phone) must
 *    not leak one restaurant's currency into another restaurant's tab or into
 *    the logged-in dashboard. Takes precedence over login scope while set.
 *
 * Default is INR for backward compatibility.
 */

interface CurrencyConfig {
  symbol: string;
  code: string;
  locale: string;
  decimals: number;
}

const DEFAULT_CURRENCY: CurrencyConfig = {
  symbol: '₹',
  code: 'INR',
  locale: 'en-IN',
  decimals: 2,
};

const VENDOR_SCOPE_KEY = 'vendorCurrency';

// Mutable login-scope currency — initialized from localStorage or default
let currentCurrency: CurrencyConfig = (() => {
  try {
    const stored = localStorage.getItem('currency');
    if (stored) {
      const parsed = JSON.parse(stored);
      return { ...DEFAULT_CURRENCY, ...parsed };
    }
  } catch {
    // ignore parse errors
  }
  return { ...DEFAULT_CURRENCY };
})();

// Vendor-scope override (guest QR context) — initialized from sessionStorage
let vendorScopedCurrency: CurrencyConfig | null = (() => {
  try {
    const stored = sessionStorage.getItem(VENDOR_SCOPE_KEY);
    if (stored) {
      return { ...DEFAULT_CURRENCY, ...JSON.parse(stored) };
    }
  } catch {
    // ignore parse errors
  }
  return null;
})();

/** The currency all read paths resolve against: vendor scope wins while set. */
function activeCurrency(): CurrencyConfig {
  return vendorScopedCurrency ?? currentCurrency;
}

/**
 * Update the login-scope currency configuration.
 * Called after login when the backend returns vendor-specific currency.
 * Clears any guest vendor-scope override — an authenticated session's currency
 * is authoritative for this tab from here on.
 */
export function setCurrency(config: { symbol: string; code: string; locale: string }) {
  currentCurrency = { ...config, decimals: 2 };
  localStorage.setItem('currency', JSON.stringify(currentCurrency));
  clearVendorScopedCurrency();
}

/**
 * Set the vendor-scope (per-tab) currency from a guest QR menu / vendor-info
 * payload. Fields may be absent on older backends — ignored unless complete.
 */
export function setVendorScopedCurrency(config: {
  currencySymbol?: string | null;
  currencyCode?: string | null;
  locale?: string | null;
}) {
  if (!config.currencySymbol || !config.currencyCode || !config.locale) {
    return;
  }
  vendorScopedCurrency = {
    symbol: config.currencySymbol,
    code: config.currencyCode,
    locale: config.locale,
    decimals: 2,
  };
  try {
    sessionStorage.setItem(VENDOR_SCOPE_KEY, JSON.stringify(vendorScopedCurrency));
  } catch {
    // storage full/unavailable — in-memory override still applies
  }
}

/** Clear the vendor-scope override (login, or leaving the guest context). */
export function clearVendorScopedCurrency() {
  vendorScopedCurrency = null;
  try {
    sessionStorage.removeItem(VENDOR_SCOPE_KEY);
  } catch {
    // ignore
  }
}

/**
 * Get the currently active currency configuration.
 */
export function getCurrency(): CurrencyConfig {
  return activeCurrency();
}

/**
 * Reset currency to default (e.g. on logout).
 */
export function resetCurrency() {
  currentCurrency = { ...DEFAULT_CURRENCY };
  localStorage.removeItem('currency');
  clearVendorScopedCurrency();
}

/**
 * Backward-compatible CURRENCY constant — reads the active currency.
 * Existing code that imports CURRENCY will get a live proxy.
 */
export const CURRENCY = new Proxy(DEFAULT_CURRENCY, {
  get(_target, prop: string) {
    return (activeCurrency() as unknown as Record<string, unknown>)[prop];
  },
});

/**
 * Format a number as currency
 * @param amount - The amount to format
 * @param options - Additional formatting options
 * @returns Formatted currency string
 */
export function formatCurrency(
  amount: number | string,
  options: {
    showSymbol?: boolean;
    showDecimals?: boolean;
    compact?: boolean;
  } = {}
): string {
  const {
    showSymbol = true,
    showDecimals = true,
    compact = false
  } = options;

  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;

  const active = activeCurrency();

  if (isNaN(numAmount)) {
    return showSymbol ? `${active.symbol}0` : '0';
  }

  // When showDecimals=false, use smart mode: hide decimals only for whole numbers.
  // Avoids "343.2" being displayed as "343" — fractional values always show full precision.
  // Compact mode keeps explicit behavior (axis ticks want clean labels).
  const hasFraction = !compact && Math.abs(numAmount - Math.trunc(numAmount)) > 1e-9;
  const effectiveDecimals = showDecimals || hasFraction ? active.decimals : 0;

  // Use Intl.NumberFormat for proper formatting
  const formatter = new Intl.NumberFormat(active.locale, {
    style: showSymbol ? 'currency' : 'decimal',
    currency: active.code,
    minimumFractionDigits: effectiveDecimals,
    maximumFractionDigits: effectiveDecimals,
    notation: compact ? 'compact' : 'standard',
  });

  return formatter.format(numAmount);
}

/**
 * Parse a currency string to number
 * @param value - Currency string to parse
 * @returns Parsed number value
 */
export function parseCurrency(value: string): number {
  // Remove currency symbol and any non-numeric characters except decimal point
  const cleanValue = value.replace(/[^0-9.-]/g, '');
  return parseFloat(cleanValue) || 0;
}

/**
 * Get currency display for input fields
 * @returns Object with symbol and position
 */
export function getCurrencyDisplay() {
  return {
    symbol: activeCurrency().symbol,
    position: 'prefix', // 'prefix' or 'suffix'
    separator: '', // Space between symbol and amount
  };
}
